import { Injectable, inject, NgZone } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { ActionPerformed, LocalNotificationSchema, LocalNotifications } from '@capacitor/local-notifications';
import { Subject } from 'rxjs';
import { VendorPushService } from './vendor-push.service';

export const VENDOR_REMINDER_CHANNEL_ID = 'vendor-reminders';
export const VENDOR_NEW_ORDER_CHANNEL_ID = 'default';

/** Android res/raw filenames (include extension). */
export const VENDOR_REMINDER_SOUND = 'vendor_order_ring.wav';
export const VENDOR_NEW_ORDER_SOUND = 'vendor_order_ring.wav';
/** Continuous ring asset (Android res/raw, one cycle; app loops natively in JS/plugin). */
export const VENDOR_ORDER_RING_SOUND = 'vendor_order_ring.wav';

/** Rotating immediate reminders (910000–910099). */
export const VENDOR_REMINDER_NOW_BASE_ID = 910000;
export const VENDOR_REMINDER_NOW_SLOT_COUNT = 100;
/** Scheduled reminders while the app is backgrounded (910101–910110). */
export const VENDOR_REMINDER_BG_BASE_ID = 910101;
/** Only schedule a short lookahead; chain reschedules after each delivery when orders remain pending. */
export const VENDOR_REMINDER_BG_COUNT = 3;
/** One-shot alert when a new order arrives on native. */
export const VENDOR_NEW_ORDER_LOCAL_ID = 900000;

export interface VendorLocalNotificationPayload {
  id?: number;
  title: string;
  body: string;
  extra?: Record<string, string>;
  delayMs?: number;
  channelId?: string;
  sound?: string;
  ongoing?: boolean;
}

/**
 * Native Android/iOS local notifications for vendor order alerts.
 * Web uses browser Notification API instead (see NewOrderAlertService).
 */
@Injectable({ providedIn: 'root' })
export class VendorLocalNotificationService {
  private readonly vendorPush = inject(VendorPushService);
  private readonly ngZone = inject(NgZone);

  private permissionGranted = false;
  private listenersRegistered = false;
  private reminderNowSlot = 0;
  private lastScheduledBgIds: number[] = [];

  /** Fires when a scheduled reminder notification is delivered (screen off / background). */
  readonly reminderDelivered$ = new Subject<void>();

  get isNative(): boolean {
    return Capacitor.isNativePlatform();
  }

  nextReminderNotificationId(): number {
    const id = VENDOR_REMINDER_NOW_BASE_ID + (this.reminderNowSlot % VENDOR_REMINDER_NOW_SLOT_COUNT);
    this.reminderNowSlot += 1;
    return id;
  }

  async ensureReady(): Promise<boolean> {
    if (!this.isNative) {
      return false;
    }
    this.registerListenersOnce();
    if (this.permissionGranted) {
      return true;
    }
    try {
      const status = await LocalNotifications.checkPermissions();
      if (status.display === 'granted') {
        this.permissionGranted = true;
        return true;
      }
      const result = await LocalNotifications.requestPermissions();
      this.permissionGranted = result.display === 'granted';
      return this.permissionGranted;
    } catch (err) {
      console.warn('[VendorLocalNotification] permission request failed:', err);
      return false;
    }
  }

  /** Show a local notification immediately (or after a short delay). */
  async show(payload: VendorLocalNotificationPayload): Promise<void> {
    if (!(await this.ensureReady())) {
      return;
    }
    const channelId = payload.channelId ?? VENDOR_REMINDER_CHANNEL_ID;
    const sound = payload.sound ?? VENDOR_REMINDER_SOUND;
    const id = payload.id ?? this.nextReminderNotificationId();
    try {
      await LocalNotifications.schedule({
        notifications: [
          {
            id,
            title: payload.title,
            body: payload.body,
            channelId,
            sound,
            ongoing: payload.ongoing ?? false,
            autoCancel: true,
            schedule: {
              at: new Date(Date.now() + (payload.delayMs ?? 50)),
              allowWhileIdle: true,
            },
            extra: payload.extra,
          },
        ],
      });
    } catch (err) {
      console.warn('[VendorLocalNotification] schedule failed:', err);
    }
  }

  /** Schedule repeating reminders every {@link intervalMs} while the app is backgrounded / screen locked. */
  async scheduleBackgroundReminders(
    intervalMs: number,
    title: string,
    body: string,
    extra?: Record<string, string>,
  ): Promise<void> {
    if (!(await this.ensureReady())) {
      return;
    }
    await this.cancelBackgroundReminders();

    const notifications: LocalNotificationSchema[] = Array.from(
      { length: VENDOR_REMINDER_BG_COUNT },
      (_, i) => ({
        id: VENDOR_REMINDER_BG_BASE_ID + i,
        title,
        body,
        channelId: VENDOR_REMINDER_CHANNEL_ID,
        sound: VENDOR_REMINDER_SOUND,
        ongoing: false,
        autoCancel: true,
        schedule: {
          at: new Date(Date.now() + (i + 1) * intervalMs),
          allowWhileIdle: true,
        },
        extra,
      }),
    );

    this.lastScheduledBgIds = notifications.map((n) => n.id);

    try {
      await LocalNotifications.schedule({ notifications });
    } catch (err) {
      console.warn('[VendorLocalNotification] background schedule failed:', err);
    }
  }

  async cancelReminderNotifications(): Promise<void> {
    if (!this.isNative) {
      return;
    }
    await this.cancelBackgroundReminders();
    try {
      const nowIds = Array.from({ length: VENDOR_REMINDER_NOW_SLOT_COUNT }, (_, i) => ({
        id: VENDOR_REMINDER_NOW_BASE_ID + i,
      }));
      await LocalNotifications.cancel({
        notifications: [...nowIds, { id: VENDOR_NEW_ORDER_LOCAL_ID }],
      });
    } catch {
      // ignore
    }
  }

  async cancelBackgroundReminders(): Promise<void> {
    if (!this.isNative) {
      return;
    }
    const ids =
      this.lastScheduledBgIds.length > 0
        ? this.lastScheduledBgIds
        : Array.from({ length: VENDOR_REMINDER_BG_COUNT }, (_, i) => VENDOR_REMINDER_BG_BASE_ID + i);
    try {
      await LocalNotifications.cancel({
        notifications: ids.map((id) => ({ id })),
      });
    } catch {
      // ignore
    }
    this.lastScheduledBgIds = [];
  }

  private registerListenersOnce(): void {
    if (this.listenersRegistered || !this.isNative) {
      return;
    }
    this.listenersRegistered = true;

    void LocalNotifications.addListener('localNotificationReceived', (notification) => {
      const extra = notification.extra as Record<string, string> | undefined;
      if (extra?.['type'] === 'order-reminder') {
        this.reminderDelivered$.next();
      }
    });

    void LocalNotifications.addListener('localNotificationActionPerformed', (action: ActionPerformed) => {
      const extra = action.notification.extra as Record<string, string> | undefined;
      this.ngZone.run(() => {
        this.vendorPush.handleNotificationTap({
          type: extra?.['type'] ?? 'new-order',
          orderId: extra?.['orderId'],
          orderNumber: extra?.['orderNumber'],
        });
      });
    });
  }
}
